/**
 * withPagePermission HOC
 * 
 * Higher-Order Component for page-level permission checks.
 * Wraps a component and shows AccessDenied if user lacks the required permission.
 * 
 * Usage:
 * ```jsx
 * import { withPagePermission, PERMISSIONS } from '../permission';
 * 
 * const MyPage = () => {
 *   return <div>Page content</div>;
 * };
 * 
 * export default withPagePermission(MyPage, PERMISSIONS.MY_PAGE_ACCESS);
 * ```
 */

import React from 'react';
import { usePermission } from '../../hooks/usePermission';
import Loader from '../../layouts/loader/Loader';
import AccessDenied from './AccessDenied';

/**
 * HOC that wraps a component with page-level permission check
 * 
 * @param {function(): *} WrappedComponent - The component to wrap
 * @param {string} requiredPermission - The permission required to view the page
 * @returns {React.Component} - The wrapped component with permission check
 */
const withPagePermission = (WrappedComponent, requiredPermission) => {
  const WithPermissionComponent = (props) => {
    const { hasPermission, loading: permissionsLoading } = usePermission();

    // Wait for permissions to load
    if (permissionsLoading) {
      return <Loader />;
    }

    // Check if user has required permission
    if (requiredPermission && !hasPermission(requiredPermission)) {
      return <AccessDenied />;
    }

    // User has permission, render the component
    return <WrappedComponent {...props} />;
  };

  // Set display name for debugging
  const wrappedName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
  WithPermissionComponent.displayName = `withPagePermission(${wrappedName})`;

  return WithPermissionComponent;
};

export default withPagePermission;
